/**
 * Decides what price-drop metadata a listing edit should write.
 *
 * The "Price Drop" badge (strikethrough old price + red pill, see
 * PedalPriceDisplay.jsx) is driven entirely by `previousPrice` on the listing
 * doc, and this module is the ONLY thing in the app that decides to set it.
 *
 * Why the relist carve-out exists: a pedal that comes back from a completed
 * trade or purchase is a different pedal in the seller's hands than the one
 * that was listed before — it changed owners. A seller who traded for a pedal
 * that had been listed at $150 and lists it themselves at $130 is not marking
 * anything down; they are listing at their own price for the first time. The
 * old flow compared against the pre-trade price anyway and shouted
 * "Price Drop $150 → $130" on Browse Pedals, which misrepresents both listings.
 *
 * Why the carve-out has to be *consumed*: nothing in the codebase ever clears
 * `tradeCompletedAt` / `purchaseCompletedAt` — every writer (markPedalsInTrade,
 * shippoTrackingWebhook, completePurchase, tradeDeliveryMonitor, …) only ever
 * sets them, so they are permanent tombstones. Keying the carve-out off their
 * mere presence would silently swallow every genuine price drop a seller ever
 * makes on that listing after any pause/unpause. So a relist stamps
 * `relistedAt`, and a completion only counts while it is NEWER than the last
 * relist. That also keeps working if the same listing is traded a second time.
 *
 * `relistedAt` exists solely for this rule. It is deliberately NOT `reactivatedAt`,
 * which is written by unrelated flows (billingListingSuspension,
 * deactivateBannedUserPedals) and is read as a display date in several places
 * that assume the shape the new-listing reactivation path writes.
 *
 * Note this is only reached when reactivation falls through to an in-place
 * update. The other reactivation route (a `needsReactivation` listing switched
 * back on with a new photo AND a new description) creates a fresh listing doc
 * with no price history at all, which is the same outcome by a different road.
 */

// A drop has to be at least 7% to earn the badge — small corrections and
// typo fixes shouldn't re-float a listing to the top of Browse Pedals.
const DROP_THRESHOLD = 0.93;

/**
 * @param {object|null} editingPedal  the listing doc as stored, or null when adding
 * @param {object} formValues         { price, active } from the form
 * @returns {{ clearDropMetadata: boolean, setDrop: boolean, setPreviousPrice: number|null,
 *            refreshListedAt: boolean, markRelisted: boolean }}
 */
export function resolvePriceDropChange(editingPedal, formValues) {
  const none = {
    clearDropMetadata: false,
    setDrop: false,
    setPreviousPrice: null,
    refreshListedAt: false,
    markRelisted: false,
  };

  if (!editingPedal) return none;

  // Number('') is 0, which would read as a drop to zero, so require a real
  // positive number on both sides. Form validation already rejects a blank
  // price; this is belt and braces.
  const oldPrice = toPrice(editingPedal.price);
  const newPrice = toPrice(formValues?.price);
  if (oldPrice == null || newPrice == null) return none;

  if (isRelistAfterOwnershipChange(editingPedal, formValues)) {
    // Fresh listing: no price history, and it earns a fresh listedAt the same
    // way the new-doc reactivation path gives one.
    return {
      clearDropMetadata: true,
      setDrop: false,
      setPreviousPrice: null,
      refreshListedAt: true,
      markRelisted: true,
    };
  }

  if (newPrice > oldPrice) {
    return { ...none, clearDropMetadata: true };
  }

  if (newPrice < oldPrice && newPrice <= oldPrice * DROP_THRESHOLD) {
    return {
      clearDropMetadata: false,
      setDrop: true,
      // Keep the original asking price across successive drops, so the
      // strikethrough always shows what the pedal first listed for.
      setPreviousPrice: editingPedal.previousPrice == null ? oldPrice : null,
      refreshListedAt: true,
      markRelisted: false,
    };
  }

  return none;
}

/**
 * True when this save is switching a listing back on after the pedal was
 * traded away or sold — the pedal came back to the seller (returned, refused,
 * a repaired trade) and is being put up again as a new listing.
 */
export function isRelistAfterOwnershipChange(editingPedal, formValues) {
  if (!editingPedal) return false;

  // `active === false`, not `!active`: everywhere else in the app a listing
  // counts as live unless the field is explicitly false, and legacy docs
  // predate the field. Only an explicitly-off listing is being switched on.
  const turningBackOn = Boolean(formValues?.active) && editingPedal.active === false;
  if (!turningBackOn) return false;

  const relistedAt = toMillis(editingPedal.relistedAt);
  const completedAt = Math.max(
    toMillis(editingPedal.tradeCompletedAt) ?? -Infinity,
    toMillis(editingPedal.purchaseCompletedAt) ?? -Infinity
  );

  if (completedAt > -Infinity) {
    return relistedAt == null || completedAt > relistedAt;
  }

  // A completion that predates those timestamp fields, or one recorded only as
  // the needsReactivation flag. In practice the form's own reactivation branch
  // intercepts this case first; kept so the rule stands on its own.
  return editingPedal.needsReactivation === true && relistedAt == null;
}

function toPrice(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Firestore Timestamp, {seconds}, Date, ISO string, or epoch ms → ms, else null. */
function toMillis(value) {
  if (value === null || value === undefined) return null;
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'number') return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

export { DROP_THRESHOLD };
